import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getVendorStatus } from '../services/subscriptionService';

const useSubscriptionStatus = () => {
  const [hasActiveSubscription, setHasActiveSubscription] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const { user } = useAuth();
  const isCheckingRef = useRef(false);
  const lastCheckTimeRef = useRef(0);

  const checkSubscriptionStatus = useCallback(async () => {
    if (isCheckingRef.current) return;
    
    // Throttle: Don't check more than once every 5 seconds
    const now = Date.now();
    if (now - lastCheckTimeRef.current < 5000) {
      return;
    }
    
    // Only proceed if user is a vendor
    if (user?.user_type !== 'vendor') {
      setHasActiveSubscription(null);
      return;
    }
    
    try {
      isCheckingRef.current = true;
      setIsChecking(true);
      lastCheckTimeRef.current = now;
      
      const response = await getVendorStatus();
      
      if (response.success) {
        const hasSubscription = response.data.has_active_subscription;
        setHasActiveSubscription(hasSubscription);
      } else {
        setHasActiveSubscription(false);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      setHasActiveSubscription(false);
    } finally {
      isCheckingRef.current = false;
      setIsChecking(false);
    }
  }, [user?.user_type]);

  // Check subscription status when user changes
  useEffect(() => {
    if (user?.user_type === 'vendor') {
      checkSubscriptionStatus();
    } else {
      setHasActiveSubscription(null);
    }
  }, [user?.user_type, user?.id, checkSubscriptionStatus]);

  return {
    hasActiveSubscription,
    isChecking,
    checkSubscriptionStatus,
    refreshSubscriptionStatus: checkSubscriptionStatus
  };
};

export default useSubscriptionStatus;
